// BUILD · DEPOT (v2-the-deep.md §11.5, §14.7, R1811, R2653): the body of
// the DEPOT pill. The rack head states the stock and the queue against
// the rack, a head for each family states the live count on the map
// against the live cap of the Depot level, and one build row for each
// kind of sea object orders one item. The server computes every figure
// (`Empire.depot`) and ships every kind (`Catalog.sea_objects`), so the
// pill mirrors no table: it reads the Depot level and the tech gate the
// way the TORPEDO rows read theirs.
//
// A family head names the live count "on the map". While an own item of
// the family is in the picture, the head is a press: each press shows the
// next own item of the family on the map, in id order, and wraps at the
// end (R3244). The overlay holds the id it showed last.

import { Building, JobKind } from '../../gen/proto/aquaphobia';
import type { DepotState, SeaObjectSpec } from '../../gen/proto/aquaphobia';
import { overlay as S } from '../../gen/strings';
import { type GameState, dur } from '../../state';
import type { SeaObjectRow } from '../../seaobjects';
import { esc } from '../esc';
import { groupThousands } from '../../locale/group';
import { type RowPress, buildRowHtml, rowIcon } from './buildrow';
import { emptyGoHtml } from './howfold';
import { storeRates } from '../storewait';

/** The items of one kind in the stock of the Depot. */
export function depotHeld(d: DepotState | undefined, kind: number): number {
  return d?.stock.find((x) => x.kind === kind)?.count ?? 0;
}

/** The items of one kind on the build queue: the count of each sea
 *  object job whose target is the kind. */
export function depotQueued(s: GameState, kind: number): number {
  let n = 0;
  for (const j of s.queue) if (j.kind === JobKind.JOB_KIND_SEA_OBJECT && j.target === kind) n += j.count;
  return n;
}

/**
 * The ORDER press of one kind and the gate that holds it. The press lays
 * down one item. The server refuses the order on the same gates
 * (`seaObjectOrder`): the Depot level, the tech gate, the rack and the
 * price. `why` is the plain reason of a lock, or ''.
 */
export function seaObjectPress(s: GameState, k: SeaObjectSpec): { press: RowPress; why: string } {
  const d = s.empire?.depot;
  const depotLocked = s.level(Building.BUILDING_DEPOT) < k.depotLevel;
  const techLocked = k.tech !== 0 && !s.hasTech(k.tech);
  const full = d !== undefined && d.stocked + 1 > d.rack;
  const why = depotLocked ? S.why_needs_depot({ level: k.depotLevel })
    : techLocked ? S.why_needs_tech({ tech: s.tech(k.tech)?.name ?? S.word_tech })
      : full ? S.why_depot_rack_full : '';
  return {
    press: {
      act: 'seaobj', id: k.id, n: 1, word: S.btn_order, cost: k.cost, have: s.res, rates: storeRates(s),
      state: why ? 'lock' : s.canAfford(k.cost) ? 'ready' : 'short',
    },
    why,
  };
}

/** One row of the pill: the kind with its stock ×N and its queue +N, the
 *  gate or the family on line 2, and ORDER. */
function seaObjectRow(s: GameState, k: SeaObjectSpec): string {
  const held = depotHeld(s.empire?.depot, k.id);
  const queued = depotQueued(s, k.id);
  const { press, why } = seaObjectPress(s, k);
  const line = k.seaMine ? S.depot_line_mine({ n: k.cargo }) : S.depot_line_deployable({ n: k.cargo });
  return buildRowHtml({
    icon: rowIcon(k.seaMine ? 'mine' : 'deployable'),
    // The name opens the build card of the kind, as on every build list
    // (R2708).
    name: `<button type="button" class="info" data-act="info" data-kind="seaobject" data-id="${k.id}"`
      + ` title="${S.info_btn_seaobject_title}">${esc(k.name)}</button>`
      + ` <span class="held" title="${S.depot_held_title}">${S.row_held({ n: groupThousands(held) })}</span>`
      + (queued > 0 ? ` <span class="held" title="${S.depot_queued_title}">${S.row_queued({ n: queued })}</span>` : ''),
    line2: esc(why || line),
    time: dur(s.buildSec(JobKind.JOB_KIND_SEA_OBJECT, k.timeSec)),
    press,
  });
}

/** The head of the deployables: the live count, then on a line of its own
 *  the Fanals and the Palisades against their own limits once the
 *  Depot level lets one stand (R3073, R3190). */
function deployableHead(d: DepotState): string {
  const head = S.depot_head_deployables({ n: d.liveDeployables, cap: d.deployablesCap });
  const limits = [
    d.laneLightsCap > 0 ? S.depot_limit_lane_lights({ l: d.liveLaneLights, lcap: d.laneLightsCap }) : '',
    d.palisadesCap > 0 ? S.depot_limit_palisades({ p: d.livePalisades, pcap: d.palisadesCap }) : '',
  ].filter((w) => w !== '').join(' · ');
  return limits === '' ? head : `${head}<span class="lim">${limits}</span>`;
}

/** The own items of one family in the picture, in id order: the sea
 *  mines (`mine`) or the deployables. */
export function depotOnMap(s: GameState, mine: boolean): SeaObjectRow[] {
  return s.seaObjects.list.filter((r) => r.own && (s.seaObject(r.kind)?.seaMine ?? false) === mine);
}

/**
 * The own item of one family that a press of its head shows next (R3244):
 * the first one whose id is above `lastId`, else the first one, so the
 * presses walk every item and start again. An item that left the picture
 * drops out of the walk. Undefined when the family has no item on the map.
 */
export function nextDepotObject(s: GameState, mine: boolean, lastId: number): SeaObjectRow | undefined {
  const rows = depotOnMap(s, mine);
  return rows.find((r) => r.id > lastId) ?? rows[0];
}

/** A family head: a press that shows the next own item on the map while
 *  one is in the picture, else the plain words (R3244). */
function familyHead(s: GameState, mine: boolean, words: string): string {
  if (depotOnMap(s, mine).length === 0) return `<h3>${words}</h3>`;
  return `<h3><button type="button" class="depot-go" data-act="depotgo" data-n="${mine ? 1 : 0}"`
    + ` title="${S.depot_show_title}">${words}</button></h3>`;
}

/** The body of the DEPOT pill. */
export function depotHtml(s: GameState): string {
  const e = s.empire;
  if (!e) return `<div class="empty">${S.empire_waiting}</div>`;
  const kinds = s.catalog?.seaObjects ?? [];
  const d = e.depot;
  if (kinds.length === 0 || !d) return `<div class="empty">${S.depot_empty}</div>`;
  let html = '';
  // A hold with no Depot reads every row LOCKED, so the line above the
  // rows says why and opens BASE, as the yard does with no Shipyard.
  if (s.level(Building.BUILDING_DEPOT) === 0) html += emptyGoHtml(S.depot_none, 'BUILD', 'BASE', { kind: 'building', id: Building.BUILDING_DEPOT });
  html += `<h3>${S.depot_head_rack({ w: d.stocked, cap: d.rack })}</h3>`;
  for (const mine of [true, false]) {
    const group = kinds.filter((k) => k.seaMine === mine);
    if (group.length === 0) continue;
    html += familyHead(s, mine, mine ? S.depot_head_mines({ n: d.liveSeaMines, cap: d.seaMinesCap }) : deployableHead(d));
    for (const k of group) html += seaObjectRow(s, k);
  }
  return html;
}
